import {
  buildLoginRedirect,
  getCurrentAuthContext,
  getCurrentPath,
} from './page-runtime';
import { escapeHtml } from './html';
import { renderStarIcons } from './skill-detail-panels';
import type { SkillReviewItem } from '../types/api';

function renderReviewForm(
  skillId: number,
  isLoggedIn: boolean,
  myReview: SkillReviewItem | null,
): string {
  if (!isLoggedIn) {
    return '' +
      '<p class="text-zinc-500 text-sm mb-4">' +
        '<a href="' + buildLoginRedirect(getCurrentPath()) + '" class="text-brand-400 hover:text-brand-300">Sign in</a> to write a review.' +
      '</p>';
  }

  const hasReview = !!myReview;
  const currentRating = myReview?.rating ?? 0;
  return '' +
    '<div class="border border-zinc-800 rounded-lg p-4 mb-4">' +
      '<h3 class="text-sm font-medium text-zinc-300 mb-3">' +
        (hasReview ? 'Update your review' : 'Write a review') +
      '</h3>' +
      '<div class="mb-3">' +
        '<p class="text-xs text-zinc-500 mb-1">Rating (required)</p>' +
        '<div class="flex gap-1" id="rating-picker">' +
          Array.from({ length: 5 }, (_, i) => {
            const val = i + 1;
            const filled = val <= currentRating;
            return '<button type="button" onclick="window.__pickRating(' + val + ')" ' +
              'class="text-2xl transition-colors rating-star ' + (filled ? 'text-amber-400' : 'text-zinc-600') + '" ' +
              'data-val="' + val + '">★</button>';
          }).join('') +
        '</div>' +
        '<input type="hidden" id="selected-rating" value="' + currentRating + '">' +
      '</div>' +
      '<div class="space-y-2 mb-3">' +
        '<input type="text" id="review-title" class="input w-full" ' +
          'placeholder="Summary (required)" value="' + escapeHtml(myReview?.title ?? '') + '">' +
        '<textarea id="review-content" class="input w-full h-20" ' +
          'placeholder="Share details about your experience (required)">' +
          escapeHtml(myReview?.content ?? '') +
        '</textarea>' +
      '</div>' +
      '<div class="flex gap-2">' +
        '<button onclick="window.__submitReview(' + skillId + (hasReview ? ', ' + myReview!.id : '') + ')" ' +
          'class="btn-primary text-sm">' +
          (hasReview ? 'Update Review' : 'Submit Review') +
        '</button>' +
        (hasReview
          ? '<button onclick="window.__deleteReview(' + skillId + ', ' + myReview!.id + ')" ' +
              'class="btn-ghost text-sm text-red-400">Delete</button>'
          : '') +
      '</div>' +
    '</div>';
}

function renderReviewsList(reviews: SkillReviewItem[]): string {
  if (!reviews.length) return '<p class="text-zinc-500 text-sm">No reviews yet. Be the first!</p>';

  return reviews.map(function(review) {
    return '<div class="border border-zinc-800 rounded-lg p-4">' +
      '<div class="flex items-start justify-between gap-3 mb-2">' +
        '<div class="flex items-center gap-2">' +
          '<span class="font-medium text-white text-sm">' + escapeHtml(review.display_name || review.username || 'unknown') + '</span>' +
          '<span class="flex gap-0.5 text-sm">' + renderStarIcons(review.rating) + '</span>' +
        '</div>' +
        '<span class="text-zinc-500 text-xs shrink-0">' + new Date(review.created_at).toLocaleDateString() + '</span>' +
      '</div>' +
      '<p class="text-sm font-medium text-zinc-200 mb-1">' + escapeHtml(review.title) + '</p>' +
      '<p class="text-sm text-zinc-400">' + escapeHtml(review.content) + '</p>' +
    '</div>';
  }).join('');
}

export async function loadStarAndReviews(skillId: number): Promise<void> {
  const isLoggedIn = getCurrentAuthContext().loggedIn;
  const reviewState = await window.SkillDetailData.loadSkillReviewState(skillId, isLoggedIn);
  if (!reviewState.ok) throw new Error(reviewState.error);
  const { rating, reviews, myRating, myReview } = reviewState.data;

  const starBtn = document.getElementById('star-btn') as HTMLButtonElement | null;
  const starCount = document.getElementById('star-count');
  const starIcon = document.getElementById('star-icon');
  if (starBtn && starCount) {
    starCount.textContent = String(rating.count);
    starBtn.style.display = '';
    if (starIcon && myRating && myRating.rating) {
      starIcon.classList.add('text-amber-400');
      starBtn.title = 'Your rating: ' + myRating.rating + '/5';
    }
  }

  const summaryEl = document.getElementById('rating-summary');
  if (summaryEl) {
    const { average, count } = rating;
    summaryEl.innerHTML = count === 0
      ? '<span class="text-zinc-500">No ratings yet</span>'
      : '<span class="flex items-center gap-2">' +
          renderStarIcons(average) +
          '<span class="text-white font-semibold ml-1">' + average.toFixed(1) + '</span>' +
          '<span class="text-zinc-400">(' + count + ' rating' + (count !== 1 ? 's' : '') + ')</span>' +
        '</span>';
  }

  const formContainer = document.getElementById('review-form-container');
  if (formContainer) {
    formContainer.innerHTML = renderReviewForm(skillId, isLoggedIn, myReview);
  }

  const listEl = document.getElementById('reviews-list');
  if (listEl) {
    listEl.innerHTML = renderReviewsList(reviews);
  }
}

export function registerSkillReviewHandlers(): void {
  window.__openRatingPanel = function(): void {
    if (!getCurrentAuthContext().loggedIn) {
      window.location.href = buildLoginRedirect(getCurrentPath());
      return;
    }
    const formContainer = document.getElementById('review-form-container');
    if (formContainer) formContainer.scrollIntoView({ behavior: 'smooth' });
  };

  window.__pickRating = function(val: number): void {
    const input = document.getElementById('selected-rating') as HTMLInputElement | null;
    if (input) input.value = String(val);
    document.querySelectorAll<HTMLElement>('.rating-star').forEach(function(el, i) {
      el.classList.toggle('text-amber-400', i < val);
      el.classList.toggle('text-zinc-600', i >= val);
    });
  };

  window.__submitReview = async function(skillId: number, reviewId?: number): Promise<void> {
    try {
      const rating = Number((document.getElementById('selected-rating') as HTMLInputElement | null)?.value);
      const title = ((document.getElementById('review-title') as HTMLInputElement | null)?.value ?? '').trim();
      const content = ((document.getElementById('review-content') as HTMLTextAreaElement | null)?.value ?? '').trim();

      if (!rating) {
        showToast('Please select a star rating', 'error');
        return;
      }
      if (!title) {
        showToast('Please enter a summary', 'error');
        return;
      }
      if (!content) {
        showToast('Please enter review content', 'error');
        return;
      }

      const data = { rating, title, content };
      const result = await window.SkillDetailData.submitSkillReview(skillId, reviewId, data);

      if (result.ok) {
        showToast(reviewId ? 'Review updated!' : 'Review submitted!');
        await loadStarAndReviews(skillId);
      } else {
        showToast(result.error || 'Failed to submit review', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    }
  };

  window.__deleteReview = async function(skillId: number, reviewId: number): Promise<void> {
    try {
      if (!confirm('Delete your review? This cannot be undone.')) return;
      const result = await window.SkillDetailData.deleteSkillReview(skillId, reviewId);
      if (result.ok) {
        showToast('Review deleted');
        await loadStarAndReviews(skillId);
      } else {
        showToast(result.error || 'Failed to delete review', 'error');
      }
    } catch (e) {
      showToast('Network error', 'error');
    }
  };
}
